import React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import { histogram, range } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import some from 'lodash.some';

import ConditionalWrapper from '../../atoms/conditional-wrapper';
import {
  AxisBottom,
  Group,
  HoverableGroup,
  RoundedBar,
  ResponsiveSVG,
} from '../../atoms/graphs';
import { horizontalStack } from '../../utils/layout';
import { BarHistogramProps, Quartile } from '../../types/graphs';
import { quartiles as _quartiles } from './utils';
import { colors, quartileColorStyles } from '../../styles';
import { ToolTip } from '../../components/tool-tip';

const noop = () => undefined;

class BarHistogram extends React.Component<BarHistogramProps, any> {
  static defaultProps: BarHistogramProps = {
    BarComponent: ({ className, height, width }) => (
      <RoundedBar {...{ className, height, width, radius: width / 3 }} />
    ),
    TooltipComponent: ({ text }) => <div>{text}</div>,
    QuartileLegend: ({ data }) => <text>{data}</text>,
    axisPadding: 10,
    data: [],
    domain: [0, 100],
    isFocused: ds => some(ds, d => false),
    height: 200,
    memberPadding: 0,
    quartileHovered: noop,
    quartileUnhovered: noop,
    showAxis: true,
    showLegend: false,
    thresholds: range(5, 100, 5),
    tooltipProps: bucket => ({ text: bucket.join(', ') }),
    width: 600,
    value: d => d,
  };

  // TODO https://nautodev.atlassian.net/browse/NAUTO-11483
  // tslint:disable-next-line:cognitive-complexity
  public render() {
    const {
      BarComponent,
      BarWrapper,
      QuartileLegend,
      TooltipComponent,
      axisPadding,
      className,
      data,
      domain,
      isFocused,
      height,
      memberPadding,
      quartileHovered,
      quartileUnhovered,
      showAxis,
      showLegend,
      thresholds,
      tooltipProps,
      activeEntityName,
      width,
      value,
    } = this.props;

    const anyFocused = isFocused(data);

    const buckets: any[] = histogram()
      .thresholds(thresholds)
      .value(value)
      .domain(domain)(data);
    const quartiles = _quartiles(buckets);

    const axisHeight = showAxis ? 25 + axisPadding : 0;
    const legendHeight = showLegend ? 40 : 0;
    const hoverHeight = height - axisHeight;
    const graphHeight = hoverHeight - legendHeight;

    const xScale = scaleLinear()
      .range([0, width])
      .domain(domain);

    const yScale = scaleLinear()
      .range([0, -graphHeight])
      .domain([0, Math.max(...buckets.map(b => b.length))]);

    const quartilesAlongGraph = horizontalStack(
      quartiles.length,
      hoverHeight,
      width,
    );
    const bucketsInQuartile = horizontalStack(
      quartiles.memberLength,
      graphHeight,
      quartilesAlongGraph.memberWidth,
      memberPadding,
    );

    return (
      <ResponsiveSVG
        className={className}
        height={height}
        width={width}
        viewBox={`0 0 ${width} ${height}`}
      >
        <g>
          <HoverableGroup className="graph" {...{ height: graphHeight, width }}>
            {graphHovered =>
              quartiles.map((quartile, i: Quartile) => {
                const hoverIn = () => quartileHovered(quartile);
                const hoverOut = () => quartileUnhovered(quartile);

                return (
                  <HoverableGroup
                    className="quartile"
                    key={`quartile-${i}`}
                    x={quartilesAlongGraph(i).x}
                    height={quartilesAlongGraph(i).height}
                    width={quartilesAlongGraph(i).width}
                    hoverIn={hoverIn}
                    hoverOut={hoverOut}
                  >
                    {quartileIsHovered => {
                      const muted =
                        (graphHovered && !quartileIsHovered) || anyFocused;

                      return [
                        showLegend ? (
                          <QuartileLegend
                            key={`legend-${i}`}
                            data={quartile}
                            quartile={i}
                            isActive={quartileIsHovered}
                            width={quartilesAlongGraph.memberWidth}
                          />
                        ) : null,
                        quartile.map((bucket, j) => {
                          const focused = isFocused(bucket);
                          const Tooltip = (
                            <TooltipComponent
                              {...{
                                ...tooltipProps(bucket),
                                entityName: activeEntityName,
                              }}
                            />
                          );
                          const classes = [
                            `quartile-${i}`,
                            muted ? 'muted' : '',
                            focused ? 'focused' : '',
                          ];
                          const barProps = {
                            className: classes.join(' '),
                            data: bucket,
                            focused,
                            muted,
                            quartile: i,
                            height: yScale(bucket.length),
                            width: bucketsInQuartile(j).width,
                          };

                          return (
                            <Group
                              className={'bucket'}
                              key={`bucket-${j}`}
                              x={bucketsInQuartile(j).x}
                              y={bucketsInQuartile(j).height + legendHeight}
                            >
                              <ToolTip svg={true} tooltip={Tooltip}>
                                <ConditionalWrapper
                                  Wrapper={BarWrapper}
                                  {...barProps}
                                >
                                  <BarComponent {...barProps} />
                                </ConditionalWrapper>
                              </ToolTip>
                            </Group>
                          );
                        }),
                      ];
                    }}
                  </HoverableGroup>
                );
              })
            }
          </HoverableGroup>
          {showAxis && (
            <AxisBottom
              scale={xScale}
              top={hoverHeight}
              padding={axisPadding}
              hideEnds={true}
            />
          )}
        </g>
      </ResponsiveSVG>
    );
  }
}

const StyledBarHistogram = styled(BarHistogram)`
  ${quartileColorStyles('fill')};

  .muted:not(.focused) {
    fill: ${colors.grey100};
  }
`;

export default StyledBarHistogram;
